import { Button, Group, Modal, Stack, Textarea, TextInput, Text, Title, ActionIcon, Select } from '@mantine/core';
import { useForm } from '@mantine/form';
import { BaseInputField } from '../BaseInput';
import { notifications } from '@mantine/notifications';
import { useEffect, useState } from 'react';
import { IconArrowBackUp, IconCheck, IconCircleCheck } from '@tabler/icons-react';
import { fieldConfigs } from './fieldConfigs';

export function FeedbackInputPage(props: { data?: BaseInputField; backToChat: () => void }) {
    const { data, backToChat } = props;
    const [opened, setOpened] = useState(false);
    const [awaitingSubmit, setAwaitingSubmit] = useState(false);

    const form = useForm<FeedbackInputField>({
        initialValues: {
            name: data?.name || '',
            mobile: data?.mobile || '',
            landline: '',
            email: data?.email || '',
            type: 'individual',
            address: '',
            invoiceCarrier: '',
            line: '',
            companyName: '',
            uniformNumber: '',
            businessAddress: '',
            invoiceAddress: '',
            feedback: ''
        },
        validate: (values) => {
            const errors: Record<string, string> = {};

            // 根據當前類型取得需要驗證的欄位
            const currentFields = fieldConfigs[values.type];

            currentFields.forEach(field => {
                const value = values[field.fieldkey as keyof FeedbackInputField];

                // 必填欄位驗證
                if (field.required && !value) {
                    errors[field.fieldkey] = `請輸入${field.label}`;
                    return;
                }

                // 特定欄位的格式驗證
                if (value) {
                    switch (field.fieldkey) {
                        case 'landline':
                            const landlineRegex = /^0\d{1,2}-?\d{7,8}$/;
                            if (!landlineRegex.test(String(value).replace(/\s|-/g, ''))) {
                                errors.landline = '請輸入正確的市話號碼格式 (例：02-88888888)';
                            }
                            break;

                        case 'mobile':
                            const phoneRegex = /^09\d{8}$/;
                            if (!phoneRegex.test(String(value).replace(/\s|-/g, ''))) {
                                errors.mobile = '請輸入正確的手機號碼格式 (例：0912345678)';
                            }
                            break;

                        case 'email':
                            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                            if (!emailRegex.test(String(value))) {
                                errors.email = '請輸入正確的電子郵件格式';
                            }
                            break;

                        case 'uniformNumber':
                            const uniformNumberRegex = /^\d{8}$/;
                            if (!uniformNumberRegex.test(String(value))) {
                                errors.uniformNumber = '請輸入正確的統一編號格式 (8位數字)';
                            }
                            break;
                    }
                }
            });

            return errors;
        }
    });

    useEffect(() => {
        if (data) {
            form.setValues({
                name: data.name || '',
                email: data.email || '',
                mobile: data.mobile || '',
                feedback: ''
            });
        }
        setOpened(true);
    }, [data]);


    const handleSubmit = async (values: FeedbackInputField) => {
        if (awaitingSubmit) return;
        setAwaitingSubmit(true);
        // 存入 Google 表單
        try {
            const formId = "1FAIpQLSfHKMbn3EtAcx9D-iEv9EHWCVHHwcnaIJ9NHdhYcIoRX3SqMw";
            const googleFormUrl = `https://docs.google.com/forms/d/e/${formId}/formResponse`;

            const formData = new FormData();
            formData.append('entry.905125100', values.type);

            // 根據當前類型的 fieldConfigs 來決定要送出的欄位
            const currentFields = fieldConfigs[values.type];

            currentFields.forEach(field => {
                if (field.entryId) {
                    const fieldkey = field.fieldkey as keyof FeedbackInputField;
                    if (values[fieldkey] !== undefined) {
                        const value = String(values[fieldkey] ?? '');
                        formData.append(field.entryId, value);
                    }
                }
            });

            // 提交到 Google 表單
            await fetch(googleFormUrl, {
                method: 'POST',
                mode: 'no-cors', // 重要：Google 表單需要 no-cors 模式
                body: formData
            });

            // 提交成功後執行下一步
            setOpened(false);

        } catch (error) {
            console.error('提交 Google 表單時發生錯誤:', error);
            notifications.show({
                title: '表單提交失敗',
                message: '請稍後再試，或聯絡客服人員。',
                color: 'red'
            });
        } finally {
            setAwaitingSubmit(false);
        }
    };
    const type = form.values.type;
    useEffect(() => {
        console.log('FeedbackInput type:', type);
    }, [type]);

    // 建立動態表單渲染函數
    const renderFormField = (field: typeof fieldConfigs.individual[0]) => {
        const props = {
            key: field.fieldkey,
            label: field.label,
            placeholder: field.placeholder,
            required: field.required,
            ...form.getInputProps(field.fieldkey)
        };

        switch (field.type) {
            case 'textarea':
                return <Textarea {...props} rows={3} />;
            case 'text':
            default:
                return <TextInput {...props} />;
        }
    };


    return <>
        <Modal
            opened={opened}
            onClose={() => { }}
            withCloseButton={false}
            centered
            size={"lg"} // xs, sm, md, lg, xl
            styles={{
                header: {
                    background: "#241E1E"
                },
                content: {
                    borderRadius: '15px',
                    background: " linear-gradient(180deg, #241E1E 0%, #09080E 100%)",
                    border: '2px solid #C09C54',
                    overflow: 'hidden',
                },
                body: {
                    padding: '2rem',
                    maxHeight: '90vh',
                    overflowY: 'auto', // 啟用垂直滾動
                    overflowX: 'hidden', // 隱藏水平滾動
                },
            }}
        >
            <Group justify="flex-start" gap="xs" style={{ cursor: 'pointer' }} onClick={() => { setOpened(false); backToChat(); }}>
                <ActionIcon
                    radius="xl"
                    size="md"
                    bg={"transparent"}
                >
                    <img src="/Stroke.svg" alt="Back" />
                </ActionIcon>
            </Group>
            <form onSubmit={form.onSubmit(handleSubmit)}>
                <Stack gap="sm">
                    <Title order={2} ta="center" c="#BF9376">
                        AI地產業務客服員 - 試用體驗
                    </Title>
                    <Text c="#FAFAFA">
                        感謝您對本AI客服的興趣！歡迎聯繫我們，取得更多說明及進一步洽詢。
                    </Text>
                    {/* 選擇使用者類型  使用mantine的Select元件*/}
                    <Select
                        label="使用者類型"
                        placeholder="請選擇使用者類型"
                        data={[
                            { value: 'individual', label: '自然人' },
                            { value: 'company', label: '公司行號' },
                        ]}
                        required
                        {...form.getInputProps('type')}
                    />
                    {/* 動態渲染表單欄位 */}
                    {fieldConfigs[type]?.map(field => renderFormField(field))}
                    <Group justify="center" mt="md">
                        <Button type="submit" color='#A77145' disabled={awaitingSubmit} px={60} bdrs={5}>送出資料</Button>
                    </Group>
                </Stack>
            </form>
        </Modal >
        <Modal
            opened={!opened}
            onClose={() => { }}
            withCloseButton={false}
            size={"620px"}
            centered
            styles={{
                header: {
                    background: "#241E1E"
                },
                content: {
                    borderRadius: '15px',
                    background: " linear-gradient(180deg, #241E1E 0%, #09080E 100%)",
                    border: '2px solid #C09C54',
                    overflow: 'hidden',
                },
                body: {
                    padding: '40px',
                },
            }}
        >
            <Group p={"40px"} justify="center">
                <ActionIcon
                    variant="filled"
                    color="#A77145"
                    radius="xl"
                    size="md"
                >
                    <IconCheck size={16} color="#FFFFFF" />
                </ActionIcon>

                <Text c="#FAFAFA" ta="center" >
                    資料已送出！<br />
                    將有專人盡快聯繫您。
                </Text>
            </Group>
            <Group mt={"25px"} maw={"145px"} mx="auto">
                <Button color='#A77145' fullWidth onClick={() => { setOpened(true); backToChat(); }}>確定</Button>
            </Group>
        </Modal>
    </>
}

export interface FeedbackInputField extends BaseInputField {
    type: 'individual' | 'company';  //自然人或公司行號
    landline: string;           // 聯絡市話
    address: string;            // 聯絡地址
    invoiceCarrier: string;           // 個人載具
    line: string;                   // Line ID
    companyName: string;        // 公司名稱
    uniformNumber: string;     // 統一編號
    businessAddress: string;   // 營業地址
    invoiceAddress: string;    // 發票地址
    feedback: string;            // 反饋意見
}